import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Check, X, ChevronDown, ChevronUp, GitCompare } from "lucide-react"
import { cn } from "@/lib/utils"
import { ToMarkdown } from "@/components/ui/tomarkdown"

export interface PendingEditData {
  article_id: number
  title_before: string
  title_after: string
  body_before_md: string
  body_after_md: string
  locale?: string
  note?: string
}

interface PendingEditReviewProps {
  data: PendingEditData
  onApprove: () => void
  onReject: () => void
  onOpenArticle?: (articleId: number) => void
  /** Where an approval writes to. Defaults to Zendesk wording. */
  destinationLabel?: string
}

// ── Minimal line-level diff ───────────────────────────────────────────────────

type DiffLine = { type: "same" | "added" | "removed"; text: string }

function lineDiff(before: string, after: string): { left: DiffLine[]; right: DiffLine[] } {
  const aLines = before.split("\n")
  const bLines = after.split("\n")

  // Build LCS table
  const m = aLines.length
  const n = bLines.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = aLines[i - 1] === bLines[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1])

  // Traceback
  const left: DiffLine[] = []
  const right: DiffLine[] = []
  let i = m, j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aLines[i - 1] === bLines[j - 1]) {
      left.unshift({ type: "same", text: aLines[i - 1] })
      right.unshift({ type: "same", text: bLines[j - 1] })
      i--; j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      left.unshift({ type: "added", text: "" }) // placeholder for alignment
      right.unshift({ type: "added", text: bLines[j - 1] })
      j--
    } else {
      left.unshift({ type: "removed", text: aLines[i - 1] })
      right.unshift({ type: "removed", text: "" }) // placeholder
      i--
    }
  }
  return { left, right }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PendingEditReview({ data, onApprove, onReject, onOpenArticle, destinationLabel }: PendingEditReviewProps) {
  const [view, setView] = useState<"diff" | "before" | "after">("diff")
  const [collapsed, setCollapsed] = useState(false)

  const titleChanged = data.title_before !== data.title_after
  const bodyChanged = data.body_before_md !== data.body_after_md

  const { left: diffLeft, right: diffRight } = lineDiff(data.body_before_md, data.body_after_md)

  const addedLines = diffRight.filter(l => l.type === "added").length
  const removedLines = diffLeft.filter(l => l.type === "removed").length

  return (
    <div className="rounded-xl border-2 border-purple-200 bg-white shadow-md overflow-hidden my-2">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-linear-to-r from-purple-50 to-blue-50 border-b border-purple-200">
        <GitCompare className="h-4 w-4 text-purple-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-purple-900">{data.note ?? "Proposed Edit"} — Review Required</span>
            {/* article_id 0 means the change isn't tied to a Zendesk article
                (e.g. a local-CMS operation), so there's no id worth showing. */}
            {data.article_id > 0 && (onOpenArticle ? (
              <button
                onClick={() => onOpenArticle(data.article_id)}
                className="inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2.5 py-0.5 text-[10px] font-semibold text-purple-700 hover:bg-purple-100 transition-colors"
                title="Open article in editor"
              >
                Article #{data.article_id}
              </button>
            ) : (
              <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700 bg-purple-50">
                Article #{data.article_id}
              </Badge>
            ))}
            {data.locale && (
              <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50">
                {data.locale}
              </Badge>
            )}
            {addedLines > 0 && (
              <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">
                +{addedLines} lines
              </Badge>
            )}
            {removedLines > 0 && (
              <Badge variant="outline" className="text-[10px] border-red-300 text-red-700 bg-red-50">
                -{removedLines} lines
              </Badge>
            )}
          </div>
          {titleChanged && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              <span className="line-through text-red-500">{data.title_before}</span>
              {" → "}
              <span className="text-green-700 font-medium">{data.title_after}</span>
            </p>
          )}
          {!titleChanged && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{data.title_before}</p>
          )}
        </div>
        <button onClick={() => setCollapsed(c => !c)} className="text-muted-foreground hover:text-foreground shrink-0">
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* View toggle */}
          <div className="flex items-center gap-1 px-4 py-2 border-b bg-muted/20">
            {(["diff", "before", "after"] as const).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "text-xs px-2.5 py-1 rounded-md font-medium transition-colors",
                  view === v
                    ? "bg-purple-100 text-purple-800"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {v === "diff" ? "Side-by-side diff" : v === "before" ? "Current" : "Proposed"}
              </button>
            ))}
          </div>

          {/* Diff body */}
          <div className="max-h-[360px] overflow-hidden">
            {view === "diff" ? (
              <div className="grid grid-cols-2 divide-x h-full">
                {/* Before column */}
                <div className="min-w-0">
                  <div className="px-3 py-1.5 bg-red-50 border-b text-[10px] font-semibold text-red-700 uppercase tracking-wide">
                    Current
                  </div>
                  <ScrollArea className="h-[300px] [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
                    <div className="font-mono text-[11px] leading-relaxed">
                      {diffLeft.map((line, i) => (
                        <div
                          key={i}
                          className={cn(
                            "px-3 py-0.5 whitespace-pre-wrap break-all",
                            line.type === "removed" ? "bg-red-50 text-red-800" : "",
                            line.type === "added" ? "bg-transparent text-transparent select-none" : "",
                          )}
                        >
                          {line.type === "removed" && <span className="text-red-400 select-none mr-1">−</span>}
                          {line.type === "same" && <span className="text-muted-foreground select-none mr-1"> </span>}
                          {line.text || " "}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {/* After column */}
                <div className="min-w-0">
                  <div className="px-3 py-1.5 bg-green-50 border-b text-[10px] font-semibold text-green-700 uppercase tracking-wide">
                    Proposed
                  </div>
                  <ScrollArea className="h-[300px] [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
                    <div className="font-mono text-[11px] leading-relaxed">
                      {diffRight.map((line, i) => (
                        <div
                          key={i}
                          className={cn(
                            "px-3 py-0.5 whitespace-pre-wrap break-all",
                            line.type === "added" ? "bg-green-50 text-green-800" : "",
                            line.type === "removed" ? "bg-transparent text-transparent select-none" : "",
                          )}
                        >
                          {line.type === "added" && <span className="text-green-500 select-none mr-1">+</span>}
                          {line.type === "same" && <span className="text-muted-foreground select-none mr-1"> </span>}
                          {line.text || " "}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-[330px] [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
                <div className="px-4 py-3 prose prose-sm max-w-none">
                  <ToMarkdown>{view === "before" ? data.body_before_md : data.body_after_md}</ToMarkdown>
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Action bar */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-muted/10">
            <p className="text-xs text-muted-foreground">
              Review the proposed changes above before they are saved to {destinationLabel ?? "Zendesk"}.
            </p>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-red-200 text-red-700 hover:bg-red-50"
                onClick={onReject}
              >
                <X className="h-3.5 w-3.5" />
                Reject
              </Button>
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                onClick={onApprove}
              >
                <Check className="h-3.5 w-3.5" />
                Approve & Save
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
